import React, { useCallback, useEffect, useRef, useState } from "react";

import Alert from "veneer/Alert";
import Drawer from "veneer/Drawer";
import { showError, showSuccess } from "veneer/SnackBar";
import Stack from "veneer/Stack";

import { incidentsService } from "cortex/ui/src/fleet/service/incidents";
import { Incident } from "cortex/ui/src/zeus/view/incidents/model";

import { IncidentCommentEditor } from "./IncidentComments";

interface CommentIncidentsDrawerProps {
  onCancel: () => void;
  open: boolean;
  selectedItems: ReadonlyArray<Incident>;
  setSelectedItems: (items: ReadonlyArray<Incident>) => void;
}

export default function CommentIncidentsDrawer({
  onCancel,
  open,
  selectedItems,
  setSelectedItems,
}: CommentIncidentsDrawerProps) {
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (open) {
      setComment("");
    }
  }, [open]);

  const handleCancel = useCallback(() => {
    if (submitting) {
      return;
    }

    setComment("");
    onCancel();
  }, [onCancel, submitting]);

  const handleSubmit = useCallback(async () => {
    const trimmedComment = comment.trim();
    if (!trimmedComment || submitting) {
      return;
    }

    setSubmitting(true);

    try {
      await incidentsService.addComments(
        selectedItems.map(({ id }) => id),
        trimmedComment,
      );

      showSuccess(`Comment added to ${selectedItems.length} incidents.`);
      setSelectedItems([]);
      setComment("");
      onCancel();
    } catch {
      showError("Failed to add comment to incidents.");
    } finally {
      if (isMountedRef.current) {
        setSubmitting(false);
      }
    }
  }, [comment, onCancel, selectedItems, setSelectedItems, submitting]);

  return (
    <Drawer
      commitDisabled={submitting || !comment.trim()}
      commitLabel="Comment"
      commitLoading={submitting}
      dataTestId={{
        cancelButton: "bulk-incident-comment-cancel",
        commitButton: "bulk-incident-comment-submit",
        drawer: "bulk-incident-comment-drawer",
      }}
      onCancel={handleCancel}
      onClose={handleCancel}
      onCommit={handleSubmit}
      open={open}
      title="Comment on Incidents"
    >
      <Stack spacing={2}>
        <Alert
          dense
          severity="info"
          title={`You are about to comment on ${selectedItems.length} incident(s).`}
        >
          The same comment will be added to all selected incidents.
        </Alert>
        <IncidentCommentEditor
          canResolve={false}
          markResolved={false}
          newComment={comment}
          onCommentChange={setComment}
          onMarkResolvedChange={() => undefined}
          onSubmit={() => void handleSubmit()}
          showSubmitButton={false}
          submitting={submitting}
        />
      </Stack>
    </Drawer>
  );
}
